import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Table,
  Button,
  Input,
  Select,
  DatePicker,
  Space,
  Tag,
  message,
  Tooltip,
  Drawer,
  Descriptions,
  Statistic,
  Row,
  Col,
  Form,
} from 'antd';
import {
  SearchOutlined,
  EyeOutlined,
  DownloadOutlined,
  DollarOutlined,
} from '@ant-design/icons';
import {
  fetchTransactionRecords,
  fetchTransactionTypes,
  setFilters,
  setPagination,
  clearError,
} from '../../features/finance/financeSlice';
import { formatDateTime, formatAmount, formatAmountWithRegion, maskPhone, getStatusColor, getStatusText } from '../../utils/index';
import { get } from '../../utils/api';
import '../../styles/third-party.css';

const { RangePicker } = DatePicker;
const { Option } = Select;

const statusTone = (status) => {
  const color = getStatusColor(status);
  if (color === 'success') return 'paid';
  if (color === 'error') return 'failed';
  if (color === 'warning') return 'processing';
  if (color === 'processing') return 'pending';
  return 'muted';
};

const TransactionRecords = () => {
  const dispatch = useDispatch();
  const { transactionRecords, transactionTypes, pagination, filters, isLoading, error } = useSelector((state) => state.finance);
  
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [detailVisible, setDetailVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [regions, setRegions] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState(null);

  // 获取流水类型列表
  useEffect(() => {
    dispatch(fetchTransactionTypes());
  }, [dispatch]);

  // 获取地区列表
  useEffect(() => {
    const fetchRegions = async () => {
      try {
        const res = await get('/admin/regions/all');
        if (res.code === 200) {
          const regionList = res.data || [];
          setRegions(regionList);
          // 默认选择中国地区（CN）
          const defaultRegion = regionList.find(r => 
            (r.region_code || r.regionCode) === 'ZMW'
          );
          if (defaultRegion) {
            setSelectedRegion(defaultRegion);
          }
        }
      } catch (error) {
        console.error('获取地区列表失败:', error);
      }
    };
    fetchRegions();
  }, []);

  // 使用 useMemo 计算请求参数
  const requestParams = React.useMemo(() => ({
    ...filters,
    page: pagination.current || 1,
    limit: pagination.pageSize || 20,
  }), [filters, pagination.current, pagination.pageSize]);
  
  // 使用 useRef 防止死循环
  const prevParamsRef = React.useRef('');
  const isLoadingRef = React.useRef(false);
  
  useEffect(() => {
    // 如果正在加载，则不重复请求
    if (isLoadingRef.current) {
      return;
    }
    
    // 深度比较参数，只有真正变化时才请求
    const paramsStr = JSON.stringify(requestParams);
    
    if (paramsStr !== prevParamsRef.current) {
      prevParamsRef.current = paramsStr;
      isLoadingRef.current = true;
      dispatch(fetchTransactionRecords(requestParams)).finally(() => {
        isLoadingRef.current = false;
      });
    }
  }, [dispatch, requestParams]);

  useEffect(() => {
    if (error) {
      message.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  // 搜索处理
  const handleSearch = (values) => {
    const newFilters = {
      ...values,
      startDate: values.dateRange?.[0]?.format('YYYY-MM-DD'),
      endDate: values.dateRange?.[1]?.format('YYYY-MM-DD'),
    };
    delete newFilters.dateRange;
    dispatch(setFilters(newFilters));
    dispatch(setPagination({ current: 1 }));
  };

  // 重置搜索
  const handleReset = () => {
    form.resetFields();
    dispatch(setFilters({}));
    dispatch(setPagination({ current: 1 }));
  };

  // 表格列配置
  const columns = [
    {
      title: '流水ID',
      dataIndex: 'id',
      key: 'id',
      width: 88,
      render: (val) => val || <span className="tp-empty">-</span>
    },
    {
      title: '用户信息',
      dataIndex: 'userName',
      key: 'userName',
      width: 180,
      render: (name, record) => (
        <div className="tp-person">
          <div className="tp-person__main">{name || <span className="tp-empty">-</span>}</div>
          <div className="tp-person__sub">{record.userPhone || <span className="tp-empty">-</span>}</div>
        </div>
      ),
    },
    {
      title: '流水类型',
      dataIndex: 'transactionType',
      key: 'transactionType',
      width: 110,
      render: (type, record) => {
        const typeTones = {
          recharge: 'paid',
          withdraw: 'failed',
          trade: 'pending',
          commission: 'modified',
          activity: 'processing',
          adjustment: 'pending',
          balance_freeze: 'processing',
        };
        const tone = typeTones[type] || 'muted';
        const text = record.transactionTypeName || type;
        return <span className={`tp-tag tp-tag--${tone}`}>{text}</span>;
      },
    },
    {
      title: '交易金额',
      dataIndex: 'amount',
      key: 'amount',
      width: 140,
      render: (amount) => {
        const isPositive = amount > 0;
        const displayAmount = selectedRegion ? formatAmountWithRegion(Math.abs(amount), selectedRegion) : formatAmount(Math.abs(amount));
        return (
          <span className={isPositive ? 'tp-num--in' : 'tp-num--out'}>
            {isPositive ? '+' : '-'}{displayAmount}
          </span>
        );
      },
    },
    {
      title: '余额变化',
      dataIndex: 'balanceChange',
      key: 'balanceChange',
      width: 180,
      render: (_, record) => {
        const beforeDisplay = selectedRegion ? formatAmountWithRegion(record.balanceBefore, selectedRegion) : formatAmount(record.balanceBefore);
        const afterDisplay = selectedRegion ? formatAmountWithRegion(record.balanceAfter, selectedRegion) : formatAmount(record.balanceAfter);
        return <div className="tp-balance">{beforeDisplay} → {afterDisplay}</div>;
      },
    },
    {
      title: '关联订单',
      dataIndex: 'relatedOrderId',
      key: 'relatedOrderId',
      width: 160,
      render: (orderId) => orderId ? <span className="tp-ellipsis" title={orderId}>{orderId}</span> : <span className="tp-empty">-</span>
    },
    {
      title: '交易状态',
      dataIndex: 'status',
      key: 'status',
      width: 88,
      render: (status) => (
        <span className={`tp-tag tp-tag--${statusTone(status)}`}>
          {getStatusText(status)}
        </span>
      ),
    },
    {
      title: '交易时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (date) => date ? <span className="tp-time">{formatDateTime(date, 'YYYY-MM-DD HH:mm')}</span> : <span className="tp-empty">-</span>
    },
    {
      title: '操作',
      key: 'action',
      width: 72,
      render: (_, record) => (
        <div className="tp-icon-actions">
          <Tooltip title="查看详情">
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleViewDetail(record)}
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  // 查看详情
  const handleViewDetail = (record) => {
    setSelectedRecord(record);
    setDetailVisible(true);
  };

  // 导出流水
  const handleExport = async () => {
    try {
      // TODO: 实现导出功能
      message.success('导出功能待实现');
    } catch (error) {
      message.error(error);
    }
  };

  // 表格选择
  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
  };

  // 分页处理
  const handleTableChange = (pagination) => {
    dispatch(setPagination({
      current: pagination.current,
      pageSize: pagination.pageSize,
    }));
  };

  return (
    <div className="tp-page">
      <div className="tp-hidden">
        <Select
          style={{ width: 360 }}
          value={selectedRegion ? (selectedRegion.region_code || selectedRegion.regionCode) : undefined}
          onChange={(value) => {
            const region = regions.find(r =>
              (r.region_code || r.regionCode) === value
            );
            setSelectedRegion(region);
          }}
          placeholder="选择地区"
        >
          {regions.map(region => {
            const regionCode = region.region_code || region.regionCode;
            const regionName = region.region_name || region.regionName;
            const currencySymbol = region.currency_symbol || region.currencySymbol;
            const currencyName = region.currency_name || region.currencyName;
            return (
              <Option key={regionCode} value={regionCode}>
                {regionCode} {regionName} {currencySymbol} {currencyName}
              </Option>
            );
          })}
        </Select>
      </div>

      <div className="tp-card tp-toolbar">
        <Form form={form} layout="inline" className="tp-filters" onFinish={handleSearch}>
          <Form.Item name="keyword">
            <Input
              placeholder="搜索用户手机号/姓名"
              prefix={<SearchOutlined />}
              className="tp-field--lg"
              allowClear
            />
          </Form.Item>
          <Form.Item name="transactionType">
            <Select placeholder="流水类型" className="tp-field--md" allowClear>
              {transactionTypes.map((type) => (
                <Option key={type.code} value={type.code}>
                  {type.nameCn}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="status">
            <Select placeholder="交易状态" className="tp-field--sm" allowClear>
              <Option value="success">成功</Option>
              <Option value="pending">处理中</Option>
              <Option value="failed">失败</Option>
            </Select>
          </Form.Item>
          <Form.Item name="dateRange">
            <RangePicker className="tp-field--date" placeholder={['开始日期', '结束日期']} />
          </Form.Item>
          <div className="tp-filters__actions">
            <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>搜索</Button>
            <Button onClick={handleReset}>重置</Button>
            <Button icon={<DownloadOutlined />} onClick={handleExport}>导出</Button>
          </div>
        </Form>
      </div>

      <div className="tp-card tp-table-card">
        <div className="tp-table-head">
          <div className="tp-filters__actions">
            <Button
              disabled={selectedRowKeys.length === 0}
              icon={<DownloadOutlined />}
            >
              批量导出
            </Button>
          </div>
          <div className="tp-table-head__count">共 {pagination.total} 条流水记录</div>
        </div>
        <Table
          columns={columns}
          dataSource={transactionRecords}
          rowKey="id"
          loading={isLoading}
          size="middle"
          rowSelection={rowSelection}
          scroll={{ x: 1280 }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `第 ${range[0]}-${range[1]} 条/共 ${total} 条`,
          }}
          onChange={handleTableChange}
        />
      </div>

      {/* 流水详情抽屉 */}
      <Drawer
        title="流水详情"
        width={600}
        open={detailVisible}
        onClose={() => setDetailVisible(false)}
      >
        {selectedRecord && (
          <div>
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col span={12}>
                <Statistic
                  title="交易金额"
                  value={selectedRecord.amount}
                  prefix={<DollarOutlined />}
                  formatter={(value) => selectedRegion ? formatAmountWithRegion(value, selectedRegion) : formatAmount(value)}
                />
                {selectedRegion && (selectedRegion.region_code || selectedRegion.regionCode) !== 'CN' && (
                  <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
                    原始: {formatAmount(selectedRecord.amount)}
                  </div>
                )}
              </Col>
              <Col span={12}>
                <Statistic
                  title="交易状态"
                  value={getStatusText(selectedRecord.status)}
                />
              </Col>
            </Row>

            <Descriptions
              title="流水信息"
              bordered
              column={1}
              size="small"
            >
              <Descriptions.Item label="流水ID">
                {selectedRecord.id}
              </Descriptions.Item>
              <Descriptions.Item label="用户信息">
                {selectedRecord.userName} ({maskPhone(selectedRecord.userPhone)})
              </Descriptions.Item>
              <Descriptions.Item label="流水类型">
                <Tag color="blue">{selectedRecord.transactionType}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="交易金额">
                <Space direction="vertical" size={0}>
                  <span>{selectedRegion ? formatAmountWithRegion(selectedRecord.amount, selectedRegion) : formatAmount(selectedRecord.amount)}</span>
                  {selectedRegion && (selectedRegion.region_code || selectedRegion.regionCode) !== 'CN' && (
                    <span style={{ fontSize: '12px', color: '#999' }}>
                      原始: {formatAmount(selectedRecord.amount)}
                    </span>
                  )}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="余额变化">
                <Space direction="vertical" size={0}>
                  <span>
                    {selectedRegion ? formatAmountWithRegion(selectedRecord.balanceBefore, selectedRegion) : formatAmount(selectedRecord.balanceBefore)} 
                    {' → '}
                    {selectedRegion ? formatAmountWithRegion(selectedRecord.balanceAfter, selectedRegion) : formatAmount(selectedRecord.balanceAfter)}
                  </span>
                  {selectedRegion && (selectedRegion.region_code || selectedRegion.regionCode) !== 'CN' && (
                    <span style={{ fontSize: '12px', color: '#999' }}>
                      原始: {formatAmount(selectedRecord.balanceBefore)} → {formatAmount(selectedRecord.balanceAfter)}
                    </span>
                  )}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="关联订单">
                {selectedRecord.relatedOrderId || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="交易状态">
                <Tag color={getStatusColor(selectedRecord.status)}>
                  {getStatusText(selectedRecord.status)}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="交易时间">
                {formatDateTime(selectedRecord.createdAt)}
              </Descriptions.Item>
              <Descriptions.Item label="备注">
                {selectedRecord.description || '-'}
              </Descriptions.Item>
            </Descriptions>
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default TransactionRecords;
